import type { Schemas } from '@kanos/api-client';
import {
  clientKindLabel,
  EmployeeBand,
  employeeBandLabel,
  InquiryService,
  inquiryServiceLabel,
  type ClientKind,
} from '@kanos/domain';
import { Button, CheckboxGroupField, ChoiceCards, SelectField, TextField } from '@kanos/ui';
import { api, failure, OutLink, ProblemAlert } from '@kanos/web-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, CircleCheck, Lock, User, Users } from 'lucide-react';
import { useState, type ComponentType, type SVGProps } from 'react';
import { Form } from 'react-aria-components';
import { useBranding } from './branding';
import { firmLegalOf, Lead, PageLoading, PortalPage } from './portal-page';

type InquiryInput = Schemas['InquiryInput'];
type PortalBranding = Schemas['PortalBranding'];

/**
 * The way as the person walks it (ADR-023, ADR-026): the inquiry here, then the firm answers -
 * the data and the identification follow on its invitation, not on this page.
 */
const PATH = ['Anfrage senden', 'Die Kanzlei meldet sich', 'Angaben und Identifizierung'] as const;

/** Where the person stands on the way - the first step is this page. */
function PathAhead() {
  return (
    <section aria-label="So geht es weiter">
      <ol className="flex flex-col gap-2 text-sm sm:flex-row sm:gap-6">
        {PATH.map((step, i) => (
          <li
            key={step}
            aria-current={i === 0 ? 'step' : undefined}
            className={
              i === 0
                ? 'flex items-center gap-2 font-semibold'
                : 'flex items-center gap-2 text-muted'
            }
          >
            <span
              aria-hidden
              className={
                i === 0
                  ? 'grid size-6 shrink-0 place-items-center rounded-full bg-accent text-xs font-bold text-accent-on'
                  : 'grid size-6 shrink-0 place-items-center rounded-full text-xs font-bold ring-1 ring-hairline ring-inset'
              }
            >
              {i + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Before a kind is chosen, the name is asked as of a person. */
const PERSON_NAME = { label: 'Ihr Name', placeholder: 'z. B. Anna Albers' };

const clientKinds: {
  id: ClientKind;
  hint: string;
  name: { label: string; placeholder: string };
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}[] = [
  {
    id: 'private',
    hint: 'Einkommensteuer, Vermögen',
    name: PERSON_NAME,
    icon: User,
  },
  {
    id: 'business',
    hint: 'GmbH, Einzelunternehmen, Personengesellschaft',
    name: { label: 'Name des Unternehmens', placeholder: 'z. B. Musterbau GmbH' },
    icon: Building2,
  },
  {
    id: 'association',
    hint: 'Gemeinnützige Organisationen',
    name: { label: 'Name der Organisation', placeholder: 'z. B. Sportverein Musterstadt e. V.' },
    icon: Users,
  },
];

const ACKNOWLEDGED = 'acknowledged';

/** The first facts the firm compares with whom it takes on (ADR-068) - all of them freely given. */
const services = InquiryService.options.map((id) => ({ id, label: inquiryServiceLabel[id] }));
const employeeBands = EmployeeBand.options.map((id) => ({ id, label: employeeBandLabel[id] }));

/** Whether this portal takes inquiries (ADR-023): the firm's plan and its legal pages decide. */
function usePortalIntake() {
  return useQuery({
    queryKey: ['portal', 'intake'],
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/v1/portal/intake');
      if (!data) throw failure('getPortalIntake', error, response.status);
      return data;
    },
  });
}

function useSubmitInquiry() {
  return useMutation({
    mutationFn: async (body: InquiryInput) => {
      const { error, response } = await api.POST('/api/v1/portal/inquiries', { body });
      if (!response.ok) throw failure('submitInquiry', error, response.status);
    },
  });
}

/**
 * The way in for a prospective client (development paper §6.1, ADR-023): step 1, who they are
 * and what it is about, sent to the firm whose portal this is. Only a firm whose plan includes
 * the portal and that named its legal notice and privacy notice takes inquiries; the person
 * confirms the firm's privacy notice.
 */
export function IntakeStart() {
  const branding = useBranding();
  const legal = firmLegalOf(branding);
  const intake = usePortalIntake();
  const [sent, setSent] = useState(false);

  return (
    <PortalPage branding={branding}>
      {branding === undefined || intake.isPending ? (
        <PageLoading />
      ) : sent ? (
        <Received firmName={branding.firmName} />
      ) : intake.isError ? (
        <ProblemAlert error={intake.error} />
      ) : intake.data.open && legal && branding.firmName ? (
        <IntakeForm privacyUrl={legal.privacyUrl} onSent={() => setSent(true)} />
      ) : (
        <Closed branding={branding} />
      )}
    </PortalPage>
  );
}

/** No firm behind this address, or a firm that has not named its legal pages yet. */
function Closed({ branding }: { branding: PortalBranding }) {
  return branding.firmName === null ? (
    <Lead title="Willkommen im Mandantenportal.">
      Bitte öffnen Sie das Portal über den Link auf der Website Ihrer Kanzlei – dann wissen wir, an
      wen Ihre Anfrage gehen soll.
    </Lead>
  ) : (
    <Lead title="Bald geht es hier los.">
      {branding.firmName} nimmt über das Portal noch keine Anfragen an. Bitte wenden Sie sich direkt
      an die Kanzlei.
    </Lead>
  );
}

function Received({ firmName }: { firmName: string | null }) {
  return (
    <section aria-live="polite" className="glass grid gap-4 rounded-lg p-6 sm:p-8">
      <CircleCheck aria-hidden className="size-10 text-success-fg" />
      <h1 className="text-3xl font-semibold tracking-[-0.03em]">Vielen Dank.</h1>
      <p className="text-muted sm:text-lg">
        Ihre Anfrage ist bei {firmName ?? 'der Kanzlei'} angekommen. Die Kanzlei meldet sich bei
        Ihnen – in der Regel per E-Mail.
      </p>
    </section>
  );
}

function IntakeForm({ privacyUrl, onSent }: { privacyUrl: string; onSent: () => void }) {
  const submit = useSubmitInquiry();
  // How long the form was open: a person needs some seconds, a machine none (ADR-023).
  const [openedAt] = useState(() => Date.now());
  const [kind, setKind] = useState<ClientKind | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [concern, setConcern] = useState('');
  const [wanted, setWanted] = useState<InquiryService[]>([]);
  const [postalCode, setPostalCode] = useState('');
  const [employees, setEmployees] = useState<EmployeeBand | null>(null);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [website, setWebsite] = useState('');
  const nameField = clientKinds.find((k) => k.id === kind)?.name ?? PERSON_NAME;

  return (
    <>
      <PathAhead />
      <Lead title="Schön, dass Sie da sind.">
        Ein paar Angaben genügen für den Anfang. Wir fragen später nur, was für Ihr Anliegen
        wirklich nötig ist.
      </Lead>
      <Form
        className="glass relative grid gap-6 rounded-lg p-5 sm:p-8"
        onSubmit={(e) => {
          e.preventDefault();
          if (kind === null) return;
          submit.mutate(
            {
              kind,
              name,
              email,
              phone: phone.trim() === '' ? null : phone,
              concern,
              services: wanted,
              postalCode: postalCode.trim() === '' ? null : postalCode.trim(),
              employees: kind === 'private' ? null : employees,
              privacyNoticeUrl: privacyUrl,
              website,
              elapsedMs: Date.now() - openedAt,
            },
            { onSuccess: onSent },
          );
        }}
      >
        <ChoiceCards
          name="client_kind"
          label="Für wen suchen Sie Beratung?"
          options={clientKinds.map(({ id, hint, icon }) => ({
            id,
            label: clientKindLabel[id],
            hint,
            icon,
          }))}
          value={kind}
          onChange={setKind}
          isRequired
          errorMessage="Bitte wählen Sie, für wen Sie Beratung suchen."
        />

        <TextField
          name="name"
          label={nameField.label}
          placeholder={nameField.placeholder}
          autoComplete={kind === 'private' || kind === null ? 'name' : 'organization'}
          minLength={2}
          maxLength={200}
          isRequired
          value={name}
          onChange={setName}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            name="email"
            label="E-Mail-Adresse"
            type="email"
            placeholder="z. B. anna.albers@example.com"
            autoComplete="email"
            maxLength={254}
            isRequired
            value={email}
            onChange={setEmail}
          />
          <TextField
            name="phone"
            label="Telefon"
            type="tel"
            placeholder="z. B. 030 1234567"
            description="Freiwillig – für einen Rückruf."
            autoComplete="tel"
            maxLength={30}
            value={phone}
            onChange={setPhone}
          />
        </div>
        <TextField
          name="concern"
          label="Ihr Anliegen"
          placeholder="z. B. Einkommensteuererklärung 2025 mit Einkünften aus Vermietung"
          multiline
          minLength={10}
          maxLength={2000}
          isRequired
          value={concern}
          onChange={setConcern}
        />

        <CheckboxGroupField
          label="Wobei dürfen wir helfen?"
          description="Freiwillig – so sieht die Kanzlei gleich, ob sie die Richtige ist."
          options={services}
          value={wanted}
          onChange={setWanted}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            name="postal_code"
            label={
              kind === 'private' || kind === null ? 'Ihre Postleitzahl' : 'Postleitzahl des Sitzes'
            }
            placeholder="z. B. 50667"
            description="Freiwillig."
            autoComplete="postal-code"
            inputMode="numeric"
            pattern="[0-9]{5}"
            maxLength={5}
            value={postalCode}
            onChange={setPostalCode}
          />
          {kind !== null && kind !== 'private' && (
            <SelectField
              label="Mitarbeitende"
              placeholder="Bitte auswählen"
              description="Freiwillig."
              options={employeeBands}
              value={employees}
              onChange={setEmployees}
            />
          )}
        </div>

        {/* For machines only: people neither see nor reach it; whatever fills it is dropped. */}
        <div aria-hidden inert className="absolute -left-[10000px] h-px w-px overflow-hidden">
          <TextField
            name="website"
            label="Website"
            placeholder="z. B. https://www.example.com"
            autoComplete="off"
            value={website}
            onChange={setWebsite}
          />
        </div>

        <div className="grid gap-2">
          <p className="px-1 text-sm text-muted">
            Wie die Kanzlei mit Ihren Angaben umgeht, steht in ihren{' '}
            <OutLink href={privacyUrl}>Datenschutzhinweisen</OutLink>.
          </p>
          <CheckboxGroupField
            label="Datenschutz"
            options={[
              {
                id: ACKNOWLEDGED,
                label: 'Ich habe die Datenschutzhinweise der Kanzlei gelesen.',
              },
            ]}
            value={acknowledged}
            onChange={setAcknowledged}
            isRequired
            errorMessage="Bitte bestätigen Sie, dass Sie die Datenschutzhinweise gelesen haben."
          />
        </div>

        {submit.error ? <ProblemAlert error={submit.error} /> : null}

        <div className="grid gap-4 pt-2 sm:flex sm:flex-row-reverse sm:items-center sm:justify-between">
          <Button
            type="submit"
            size="lg"
            className="w-full sm:w-auto sm:px-10"
            isPending={submit.isPending}
          >
            Anfrage senden
          </Button>
          <p className="flex items-center justify-center gap-2 text-sm text-muted">
            <Lock aria-hidden className="size-4 shrink-0" />
            Verschlüsselt übertragen, gespeichert in Deutschland.
          </p>
        </div>
      </Form>
    </>
  );
}
